/**
 * Price display and comparison.
 *
 * The rule this file enforces at every turn: **a missing price is not zero.**
 * A quote-only supplier is a real supplier and appears in the table; it simply
 * contributes no number, and the interface says so in words rather than
 * rendering ₦0 and hoping nobody notices.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowUpRightIcon, ScaleIcon, TrendDownIcon, TrendUpIcon,
} from '@/components/ui/icons'
import { Badge, cx, EmptyState } from '@/components/ui/primitives'
import { FreshnessTag, SourceChip, VerificationBadge } from '@/components/data/trust'
import {
  formatDate, formatMoney, formatNumber, formatPercent, humanise, UNKNOWN,
} from '@/utils/format'
import type { PriceOffer, PricePoint, PriceStatistics } from '@/types/api'

/* -- A single amount ------------------------------------------------------- */
export function PriceValue({
  offer, size = 'md', className,
}: {
  offer: Pick<PriceOffer, 'amount' | 'min_amount' | 'max_amount' | 'currency' | 'price_type'>
  size?: 'sm' | 'md' | 'lg'
  className?: string
}) {
  const currency = offer.currency ?? 'NGN'
  const sizeClass = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-2xl',
  }[size]

  // "Price on request" is a real answer, and the most honest thing to show.
  if (offer.price_type === 'quote' || (!offer.amount && !offer.min_amount)) {
    return (
      <span
        className={cx('font-medium text-[var(--text-tertiary)]', sizeClass, className)}
        title="This supplier quotes on request rather than publishing a price."
      >
        On request
      </span>
    )
  }

  if (offer.min_amount && offer.max_amount && offer.min_amount !== offer.max_amount) {
    return (
      <span className={cx('font-semibold tabular', sizeClass, className)}>
        {formatMoney(offer.min_amount, currency)}
        <span className="mx-1 font-normal text-[var(--text-tertiary)]">–</span>
        {formatMoney(offer.max_amount, currency)}
      </span>
    )
  }

  return (
    <span className={cx('font-semibold tabular', sizeClass, className)}>
      {offer.price_type === 'starting_from' ? (
        <span className="mr-1 text-xs font-normal text-[var(--text-tertiary)]">from</span>
      ) : null}
      {formatMoney(offer.amount ?? offer.min_amount, currency)}
    </span>
  )
}

/* -- Comparison table ------------------------------------------------------ */
/**
 * Who sells this, and for how much — spec §43, §44.
 *
 * The single most valuable view in the product. Three details make it truthful
 * rather than merely tidy:
 *
 * 1. The cheapest comparable offer is marked, and only among *comparable* ones.
 * 2. Quote-only suppliers are listed but visually separated from the ranking.
 * 3. Every row carries its source and its age, so a stale price cannot pass as
 *    a current one.
 */
export function SupplierTable({
  suppliers, className,
}: {
  suppliers: PriceOffer[]
  className?: string
}) {
  if (suppliers.length === 0) {
    return (
      <EmptyState
        icon={<ScaleIcon size={18} />}
        title="No suppliers recorded yet"
        description="Nothing has been published for this product. Prices appear once a source has been crawled and reviewed."
      />
    )
  }

  const comparable = suppliers.filter((offer) => offer.is_comparable)
  const quoteOnly = suppliers.filter((offer) => !offer.is_comparable)

  const cheapest = comparable.reduce<PriceOffer | null>((best, offer) => {
    if (!offer.amount) return best
    if (!best?.amount) return offer
    return Number(offer.amount) < Number(best.amount) ? offer : best
  }, null)

  return (
    <div className={cx('overflow-x-auto', className)}>
      <table className="w-full min-w-[46rem] text-sm">
        <thead>
          <tr className="border-b border-[var(--border-default)] text-left">
            <th className="pb-2 pr-4 field-label font-semibold">Supplier</th>
            <th className="pb-2 px-4 field-label font-semibold">Location</th>
            <th className="pb-2 px-4 field-label font-semibold text-right">Price</th>
            <th className="pb-2 px-4 field-label font-semibold">Terms</th>
            <th className="pb-2 px-4 field-label font-semibold">Availability</th>
            <th className="pb-2 pl-4 field-label font-semibold">Source</th>
          </tr>
        </thead>

        <tbody className="divide-hairline">
          {comparable.map((offer) => (
            <SupplierRow
              key={`${offer.business?.uuid}-${offer.price_type}-${offer.unit}`}
              offer={offer}
              isCheapest={offer === cheapest}
            />
          ))}
        </tbody>

        {quoteOnly.length > 0 ? (
          <tbody className="divide-hairline">
            <tr>
              <td colSpan={6} className="pt-4 pb-1">
                <p className="text-xs text-[var(--text-tertiary)]">
                  {quoteOnly.length} supplier{quoteOnly.length === 1 ? '' : 's'} quote
                  {quoteOnly.length === 1 ? 's' : ''} on request. Listed, but excluded
                  from the averages below — a price we do not have is not a price of zero.
                </p>
              </td>
            </tr>
            {quoteOnly.map((offer) => (
              <SupplierRow
                key={`${offer.business?.uuid}-quote`}
                offer={offer}
                isCheapest={false}
              />
            ))}
          </tbody>
        ) : null}
      </table>
    </div>
  )
}

function SupplierRow({ offer, isCheapest }: { offer: PriceOffer; isCheapest: boolean }) {
  const business = offer.business
  return (
    <tr className="group transition-colors hover:bg-[var(--surface-hover)]">
      <td className="py-3 pr-4 align-top">
        <div className="flex items-start gap-2">
          {isCheapest ? (
            <Badge tone="positive" size="xs" className="mt-0.5 shrink-0">
              Lowest
            </Badge>
          ) : null}
          <div className="min-w-0">
            {business ? (
              <Link
                to={business.canonical_path}
                className="block truncate font-medium text-[var(--text-primary)] hover:text-[var(--accent-text)]"
              >
                {business.name}
              </Link>
            ) : (
              <span className="text-[var(--text-tertiary)]">{UNKNOWN}</span>
            )}
            {business ? (
              <div className="mt-1 flex flex-wrap items-center gap-1">
                <VerificationBadge status={business.verification_status} size="xs" />
                <span className="text-2xs text-[var(--text-tertiary)]">
                  {humanise(business.business_type)}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </td>

      <td className="px-4 py-3 align-top text-[var(--text-secondary)]">
        {business?.location ?? UNKNOWN}
      </td>

      <td className="px-4 py-3 align-top text-right">
        <PriceValue offer={offer} />
        {offer.unit ? (
          <p className="mt-0.5 text-2xs text-[var(--text-tertiary)]">per {offer.unit}</p>
        ) : null}
      </td>

      <td className="px-4 py-3 align-top">
        <div className="flex flex-col gap-1">
          <Badge tone="neutral" size="xs">
            {humanise(offer.price_type)}
          </Badge>
          {/* Wholesale pricing without its MOQ is not comparable to retail. */}
          {offer.minimum_quantity && offer.minimum_quantity > 1 ? (
            <span className="text-2xs text-[var(--text-tertiary)]">
              min {formatNumber(offer.minimum_quantity)}
            </span>
          ) : null}
        </div>
      </td>

      <td className="px-4 py-3 align-top">
        <AvailabilityBadge value={offer.availability} />
      </td>

      <td className="pl-4 py-3 align-top">
        <div className="flex flex-col items-start gap-1">
          <SourceChip
            url={offer.source_url}
            observedAt={offer.observed_at}
            confidence={offer.confidence}
          />
          <FreshnessTag days={offer.age_days} observedAt={offer.observed_at} />
        </div>
      </td>
    </tr>
  )
}

export function AvailabilityBadge({ value }: { value: PriceOffer['availability'] }) {
  const tone =
    value === 'in_stock' ? 'positive'
    : value === 'out_of_stock' || value === 'discontinued' ? 'negative'
    : value === 'preorder' ? 'info'
    : 'neutral'
  return (
    <Badge tone={tone} size="xs">
      {humanise(value)}
    </Badge>
  )
}

/* -- Statistics ------------------------------------------------------------ */
/**
 * Aggregates, one card per (currency, unit) group.
 *
 * Grouping is not presentation — it is correctness. ₦4,500 a bag and ₦95,000 a
 * tonne are both cement, and an average across them describes nothing at all.
 */
export function PriceStatisticsCards({ statistics }: { statistics: PriceStatistics[] }) {
  if (statistics.length === 0) return null

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {statistics.map((group) => (
        <div
          key={`${group.currency}-${group.unit}`}
          className="panel p-4"
        >
          <div className="flex items-baseline justify-between">
            <p className="field-label">
              {group.currency} · per {group.unit}
            </p>
            <span className="text-2xs text-[var(--text-tertiary)]">
              {group.supplier_count} supplier{group.supplier_count === 1 ? '' : 's'}
            </span>
          </div>

          <p className="mt-2 text-2xl font-semibold tabular text-[var(--text-primary)]">
            {formatMoney(group.lowest, group.currency)}
            <span className="ml-1.5 text-xs font-normal text-[var(--text-tertiary)]">
              lowest
            </span>
          </p>

          <dl className="mt-3 space-y-1.5 text-xs">
            <div className="flex justify-between">
              <dt className="text-[var(--text-tertiary)]">Average</dt>
              <dd className="tabular text-[var(--text-secondary)]">
                {formatMoney(group.average, group.currency)}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-[var(--text-tertiary)]">Highest</dt>
              <dd className="tabular text-[var(--text-secondary)]">
                {formatMoney(group.highest, group.currency)}
              </dd>
            </div>
            {group.spread_percent ? (
              <div className="flex justify-between">
                <dt className="text-[var(--text-tertiary)]">Spread</dt>
                <dd className="tabular font-medium text-[var(--color-caution)]">
                  {formatPercent(group.spread_percent)}
                </dd>
              </div>
            ) : null}
          </dl>

          {group.cheapest_supplier ? (
            <Link
              to={group.cheapest_supplier.canonical_path}
              className="mt-3 flex items-center gap-1 text-xs text-[var(--accent-text)] hover:underline"
            >
              Cheapest: {group.cheapest_supplier.name}
              <ArrowUpRightIcon size={11} />
            </Link>
          ) : null}
        </div>
      ))}
    </div>
  )
}

/* -- Trend ----------------------------------------------------------------- */
/**
 * A price series over time.
 *
 * The previous version of this chart was wrong in three ways that are worth
 * recording, because each is easy to reintroduce:
 *
 * **It was stretched.** `preserveAspectRatio="none"` on a 100-unit-wide viewBox
 * scaled the horizontal axis about eleven times more than the vertical, so the
 * point markers rendered as flat ellipses and every slope was exaggerated. The
 * viewBox is now in real units and scales uniformly.
 *
 * **Its x-axis was not time.** Points were spaced evenly by array index, so an
 * observation from yesterday and one from two months ago sat the same distance
 * apart. A time series that does not plot time misstates the shape of the
 * market. Positions now come from the dates.
 *
 * **It had no y-axis.** With the range floated to fit the data, a 0.5% drift
 * and a 50% collapse drew exactly the same picture, and nothing on screen let a
 * reader tell them apart. There are now labelled ticks on a rounded scale.
 *
 * **Its range band interpolated across unobserved days.** A filled low-to-high
 * area is right for a dense series and a fabrication for a sparse one: with
 * four observations across ninety days it drew a smooth wedge over eighty-six
 * days nobody looked at. The spread is now a whisker on each observed date
 * whenever the gaps are wide — see `sparse` below.
 *
 * The line stays in the accent hue whether prices rose or fell. Colour follows
 * the entity, not its direction — a chart that repaints itself green or red is
 * one where the reader has to check the legend to know what they are looking
 * at. Direction is carried by the delta, which has a glyph as well as a colour.
 */
export function PriceTrend({
  series, changePercent, currency = 'NGN', height = 260,
}: {
  series: PricePoint[]
  changePercent?: string | null
  currency?: string
  height?: number
}) {
  const [view, setView] = useState<'chart' | 'table'>('chart')
  const [active, setActive] = useState<number | null>(null)

  const points = series.filter((point) => point.average !== null)

  if (points.length < 2) {
    return (
      <div className="flex h-24 items-center justify-center rounded-lg border border-dashed border-[var(--border-subtle)]">
        <p className="text-xs text-[var(--text-tertiary)]">
          Not enough observations yet to show a trend.
        </p>
      </div>
    )
  }

  const width = 720
  const pad = { top: 16, right: 16, bottom: 28, left: 64 }
  const plotWidth = width - pad.left - pad.right
  const plotHeight = height - pad.top - pad.bottom

  const values = points.map((point) => Number(point.average))
  const lows = points.map((point) => Number(point.low ?? point.average))
  const highs = points.map((point) => Number(point.high ?? point.average))
  const times = points.map((point) => Date.parse(point.date))

  const scale = niceScale(Math.min(...lows), Math.max(...highs))
  const spanMs = (times[times.length - 1] - times[0]) || 1

  const x = (index: number) =>
    pad.left + ((times[index] - times[0]) / spanMs) * plotWidth
  const y = (value: number) =>
    pad.top + plotHeight - ((value - scale.min) / (scale.max - scale.min)) * plotHeight

  const line = values
    .map((value, index) => `${index === 0 ? 'M' : 'L'}${x(index)},${y(value)}`)
    .join(' ')

  // The band is the high line left-to-right, then the low line back again,
  // closed — one filled area rather than two strokes.
  const band =
    highs.map((value, index) => `${index === 0 ? 'M' : 'L'}${x(index)},${y(value)}`).join(' ') +
    lows
      .map((_, index) => {
        const reversed = points.length - 1 - index
        return `L${x(reversed)},${y(lows[reversed])}`
      })
      .join(' ') +
    ' Z'

  // Is a filled band an honest shape for this data?
  //
  // A band between the daily low and high is standard for a dense series. It
  // stops being standard the moment the observations are far apart, because the
  // fill then asserts a spread on every day in between — days nobody looked at.
  // On this product it drew a smooth wedge across ~86 unobserved days from four
  // real ones, which is the single most misleading mark on the page.
  //
  // So the shape follows the data's density: if the widest gap between two
  // observations is more than a sixth of the plotted window, the spread is drawn
  // as a whisker standing on each observed date and nothing at all in between.
  const gaps = times.slice(1).map((time, index) => time - times[index])
  const sparse = gaps.length === 0 || Math.max(...gaps) > spanMs / 6

  const change = changePercent ? Number(changePercent) : null
  const rising = change !== null && change > 0
  const last = points[points.length - 1]
  const cursor = active === null ? null : points[active]

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xl font-semibold text-[var(--text-primary)]">
            {formatMoney(String(values[values.length - 1]), currency)}
          </p>
          <p className="text-2xs text-[var(--text-tertiary)]">
            average of {last.observations} observation
            {last.observations === 1 ? '' : 's'} on {formatDate(last.date)}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {change !== null ? (
            <span
              className={cx(
                'inline-flex items-center gap-1 text-sm font-medium tabular-nums',
                // Rising is the loss colour: this is a buyer's tool, and a
                // price increase is bad news for the person reading it.
                rising ? 'text-[var(--chart-loss)]' : 'text-[var(--chart-gain)]',
              )}
              title={
                rising
                  ? 'Prices have risen over this period'
                  : 'Prices have fallen over this period'
              }
            >
              {rising ? <TrendUpIcon size={14} /> : <TrendDownIcon size={14} />}
              {formatPercent(change, { signed: true })}
            </span>
          ) : null}

          {/* The table is the chart's WCAG-clean twin: every value the plot
              encodes is readable as text, so the tooltip enhances and never
              gates. */}
          <div className="flex rounded-full border border-[var(--border-subtle)] p-0.5">
            {(['chart', 'table'] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setView(option)}
                aria-pressed={view === option}
                className={cx(
                  'rounded-full px-2.5 py-0.5 text-2xs capitalize transition-colors',
                  view === option
                    ? 'bg-[var(--surface-active)] text-[var(--text-primary)]'
                    : 'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]',
                )}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      </div>

      {view === 'table' ? (
        <div className="overflow-x-auto">
        <table className="mt-4 w-full min-w-[24rem] text-sm">
          <caption className="sr-only">
            Average, lowest and highest published price per observation day.
          </caption>
          <thead>
            <tr className="border-b border-[var(--border-default)] text-left">
              <th scope="col" className="pb-1.5 field-label font-semibold">Date</th>
              <th scope="col" className="pb-1.5 field-label font-semibold text-right">Average</th>
              <th scope="col" className="pb-1.5 field-label font-semibold text-right">Low</th>
              <th scope="col" className="pb-1.5 field-label font-semibold text-right">High</th>
              <th scope="col" className="pb-1.5 field-label font-semibold text-right">Offers</th>
            </tr>
          </thead>
          <tbody className="divide-hairline">
            {points.map((point) => (
              <tr key={point.date}>
                <td className="py-1.5 text-[var(--text-secondary)]">{formatDate(point.date)}</td>
                <td className="py-1.5 text-right tabular-nums text-[var(--text-primary)]">
                  {formatMoney(point.average, currency)}
                </td>
                <td className="py-1.5 text-right tabular-nums text-[var(--text-secondary)]">
                  {formatMoney(point.low, currency)}
                </td>
                <td className="py-1.5 text-right tabular-nums text-[var(--text-secondary)]">
                  {formatMoney(point.high, currency)}
                </td>
                <td className="py-1.5 text-right tabular-nums text-[var(--text-tertiary)]">
                  {point.observations}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      ) : (
        <div className="relative mt-3">
          <svg
            viewBox={`0 0 ${width} ${height}`}
            className="w-full"
            role="img"
            aria-label={`Price from ${formatDate(points[0].date)} to ${formatDate(last.date)}`}
            onMouseLeave={() => setActive(null)}
          >
            {/* Gridlines: solid hairlines one step off the surface, never
                dashed — dashing reads as "projection" when it is just a grid. */}
            {scale.ticks.map((tick) => (
              <g key={tick}>
                <line
                  x1={pad.left} x2={width - pad.right} y1={y(tick)} y2={y(tick)}
                  stroke="var(--chart-grid)" strokeWidth="1"
                />
                <text
                  x={pad.left - 8} y={y(tick)}
                  textAnchor="end" dominantBaseline="middle"
                  className="fill-[var(--text-tertiary)] text-[10px] tabular-nums"
                >
                  {compact(tick)}
                </text>
              </g>
            ))}

            {/* The observed range. A wash where observations are dense enough
                that interpolating between them is a fair claim; a whisker on
                each observed date where they are not. */}
            {sparse ? (
              points.map((point, index) => (
                highs[index] > lows[index] ? (
                  <line
                    key={`range-${point.date}`}
                    x1={x(index)} x2={x(index)}
                    y1={y(highs[index])} y2={y(lows[index])}
                    stroke="var(--accent)" strokeWidth="2" strokeOpacity="0.28"
                    strokeLinecap="round"
                  />
                ) : null
              ))
            ) : (
              <path d={band} fill="var(--accent)" opacity="0.12" />
            )}

            <path
              d={line} fill="none" stroke="var(--accent)" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"
            />

            {points.map((point, index) => (
              <g key={point.date}>
                <circle
                  cx={x(index)} cy={y(values[index])} r="4"
                  fill="var(--accent)"
                  stroke="var(--surface-panel)" strokeWidth="2"
                />
                {/* A hit target far larger than the mark, so a 4px dot is not
                    something the reader has to land on dead-centre. */}
                <circle
                  cx={x(index)} cy={y(values[index])} r="16"
                  fill="transparent"
                  onMouseEnter={() => setActive(index)}
                  onFocus={() => setActive(index)}
                  onBlur={() => setActive(null)}
                  tabIndex={0}
                  role="img"
                  aria-label={`${formatDate(point.date)}: ${formatMoney(point.average, currency)} average, ${formatMoney(point.low, currency)} to ${formatMoney(point.high, currency)}, ${point.observations} offers`}
                />
              </g>
            ))}

            {/* Only the endpoint is labelled. A value beside every point is
                chaos and goes unread. */}
            <text
              x={x(points.length - 1)} y={y(values[values.length - 1]) - 12}
              textAnchor="end"
              className="fill-[var(--text-secondary)] text-[10px] font-medium tabular-nums"
            >
              {compact(values[values.length - 1])}
            </text>

            {cursor && active !== null ? (
              <line
                x1={x(active)} x2={x(active)} y1={pad.top} y2={pad.top + plotHeight}
                stroke="var(--border-strong)" strokeWidth="1"
              />
            ) : null}

            {points.map((point, index) => (
              <text
                key={`label-${point.date}`}
                x={x(index)} y={height - 8}
                textAnchor={index === 0 ? 'start' : index === points.length - 1 ? 'end' : 'middle'}
                className="fill-[var(--text-tertiary)] text-[10px]"
              >
                {formatDate(point.date, { short: true })}
              </text>
            ))}
          </svg>

          {cursor ? (
            <div
              className="pointer-events-none absolute -translate-x-1/2 -translate-y-full rounded-md border border-[var(--border-default)] bg-[var(--surface-raised)] px-2.5 py-1.5 text-2xs shadow-md"
              style={{
                left: `${(x(active!) / width) * 100}%`,
                top: `${(y(values[active!]) / height) * 100}%`,
              }}
            >
              <p className="font-medium text-[var(--text-primary)]">
                {formatMoney(cursor.average, currency)}
              </p>
              <p className="text-[var(--text-tertiary)]">
                {formatMoney(cursor.low, currency)} – {formatMoney(cursor.high, currency)}
                {' · '}
                {cursor.observations} offer{cursor.observations === 1 ? '' : 's'}
              </p>
              <p className="text-[var(--text-tertiary)]">{formatDate(cursor.date)}</p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

/**
 * A rounded y-scale that contains the data.
 *
 * Ticks land on 1/2/5×10ⁿ so the axis reads 9,000 / 9,500 / 10,000 rather than
 * 8,428 / 9,119 / 9,810. The domain is padded outward to the next tick, which
 * also stops the highest point sitting exactly on the top edge.
 */
/**
 * A rounded axis domain and its ticks.
 *
 * The ladder includes 2.5 and the default tick count is five, and both are
 * there for the same reason. With a 1/2/5/10 ladder, a span whose rough step
 * lands just above 5 jumps straight to 10 — which then rounds the domain out by
 * up to a full step at each end. Prices sitting between ₦8,300 and ₦10,500 were
 * drawn on an ₦8,000–₦11,000 axis: a quarter of the plot was empty and the
 * shape of the series was flattened into it.
 */
function niceScale(low: number, high: number, count = 5) {
  const span = high - low || Math.abs(high) || 1
  const rough = span / count
  const magnitude = 10 ** Math.floor(Math.log10(rough))
  const normalised = rough / magnitude
  const step =
    (normalised <= 1 ? 1
      : normalised <= 2 ? 2
      : normalised <= 2.5 ? 2.5
      : normalised <= 5 ? 5
      : 10) * magnitude

  const min = Math.floor(low / step) * step
  const max = Math.ceil(high / step) * step
  const ticks: number[] = []
  for (let tick = min; tick <= max + step / 2; tick += step) ticks.push(tick)
  return { min, max, ticks }
}

/** Axis labels: 10,500 rather than ₦10,500.00 — the header carries the unit. */
function compact(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 })
}
